// src/context/PermissionsContext.js
import { createContext, useState, useEffect, useContext } from "react";
import { useAuth } from "./AuthContext";
import useAxios from "../utils/useAxios";
import { baseURL } from "../utils/config";
import { ROLES } from "../utils/roles";

const PermissionsContext = createContext();

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const axiosInstance = useAxios();

  // ⚠️ On démarre RESTRICTIF (false) et pas permissif (true).
  // Le "vrai" accès total pour les rôles sans notion de profil
  // (SUPER_ADMIN, etc.) est géré via l'early-return ci-dessous,
  // pas via une valeur par défaut optimiste.
  const [accesTotal, setAccesTotal] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [profil, setProfil] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  const fetchPermissions = async () => {
    // SUPER_ADMIN n'a pas d'établissement (donc pas de slug) : la route
    // /mes-permissions/ est scopée par établissement, il ne peut donc pas
    // l'appeler. On lui laisse accès total par défaut, sans requête.
    // Seul ADMINISTRATEUR a une notion de profil restreint.
    if (!isAuthenticated || user?.role !== ROLES.ADMINISTRATEUR) {
      setAccesTotal(true);
      setPermissions([]);
      setProfil(null);
      setLoadingPermissions(false);
      return;
    }

    const slug = user?.etablissement_slug;
    if (!slug) {
      // Ne devrait pas arriver pour un ADMINISTRATEUR, mais on ne bloque
      // jamais l'accès sur une donnée manquante côté front.
      setAccesTotal(true);
      setPermissions([]);
      setProfil(null);
      setLoadingPermissions(false);
      return;
    }

    setLoadingPermissions(true);
    try {
      const response = await axiosInstance.get(`${baseURL}/${slug}/mes-permissions/`);
      setAccesTotal(response.data.acces_total);
      setPermissions(response.data.permissions || []);
      setProfil(response.data.profil);
    } catch (error) {
      console.error("Erreur lors du chargement des permissions:", error);
      // Erreur réseau/serveur : on ne bloque pas l'admin, accès total par défaut.
      setAccesTotal(true);
      setPermissions([]);
      setProfil(null);
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.role, user?.etablissement_slug]);

  // ⚠️ Tant qu'on ne connaît pas encore la réponse du serveur, canSee ne
  // doit RIEN affirmer de positif : on renvoie false (sauf code vide,
  // ex: l'onglet ACCUEIL qui n'a pas de code de permission).
  const canSee = (code) => {
    if (!code) return true;
    if (loadingPermissions) return false;
    return accesTotal || permissions.includes(code);
  };

  const contextData = {
    accesTotal,
    permissions,
    profil,
    loadingPermissions,
    canSee,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={contextData}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;